import React from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { calculateGlobalStats, formatCurrency } from '../utils/helpers';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'transactions', label: 'Transactions', icon: '💳' },
  { id: 'people', label: 'People', icon: '👥' },
  { id: 'categories', label: 'Categories', icon: '📁' },
  { id: 'recurring', label: 'Recurring', icon: '🔄' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

function Sidebar({ activeTab, setActiveTab, isOpen, setIsOpen }) {
  const { state } = useFinance();
  const stats = calculateGlobalStats(state);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 bg-slate-800 border-r border-slate-700
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-700">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <span className="text-xl">💰</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">FinanceTracker</h1>
            <p className="text-xs text-slate-400">Manage your money</p>
          </div>
        </div>

        {/* Balance Card */}
        <div className="mx-4 mt-4 p-4 rounded-xl bg-gradient-to-br from-primary-600 to-primary-800 shadow-lg">
          <p className="text-primary-200 text-sm font-medium">Total Net Worth</p>
          <p className="text-2xl font-bold text-white mt-1">
            {formatCurrency(stats.netWorth, state.currency)}
          </p>
          <div className="mt-3 pt-3 border-t border-primary-500/30 grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-primary-200">Lent Out</p>
              <p className="text-sm font-semibold text-orange-300">
                {formatCurrency(stats.totalLent, state.currency)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-200">Borrowed</p>
              <p className="text-sm font-semibold text-green-300">
                {formatCurrency(stats.totalBorrowed, state.currency)}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setIsOpen(false);
              }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-xl
                font-medium transition-all duration-200
                ${activeTab === item.id
                  ? 'bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/20'
                  : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }
              `}
            >
              <span className="text-xl">{item.icon}</span>
              <span>{item.label}</span>
              {activeTab === item.id && (
                <div className="ml-auto w-2 h-2 rounded-full bg-primary-400" />
              )}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700">
          <div className="text-center">
            <p className="text-xs text-slate-500">Made with ❤️</p>
            <p className="text-xs text-slate-600 mt-1">v1.1.0</p>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
